/**
 * The two brand-attribute boxes of the store page's About section (SEO plan 2026-10-24 §R2.3): "Service Snapshot"
 * (components/TechnicalSpecs, collapsed table) and "Service Highlights" (StorePage, Best For / Support / Guarantee / Feature).
 * The template used to fill a missing attribute with a default ("Money Back", "Standard"...), stating things nobody sourced.
 * Now each box renders only its rows with a real value (present, not blank, not the default text), and a box with fewer
 * than MIN_REAL_ROWS real rows is not rendered at all (a single orphan row looks broken).
 */

/** Service Highlights rows: attribute -> the default the template shows without it. */
export const SERVICE_HIGHLIGHTS_DEFAULTS = {
    user_level: "General Use",
    support_type: "Standard",
    financial_hook: "Money Back",
    killer_feature: "Reliability",
} as const;

/** Service Snapshot (TechnicalSpecs) rows: attribute -> the default the template shows without it. */
export const SERVICE_SNAPSHOT_DEFAULTS = {
    core_identity: "Digital Service",
    market_position: "Market Leader",
    user_level: "Entrepreneurs & Businesses",
    platform_tech: "Modern Cloud Infrastructure",
    killer_feature: "High Reliability",
    financial_hook: "Competitive",
    product_list: "Subscriptions",
    support_type: "24/7 Expert Support",
} as const;

const norm = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();

/** What the row renders as text: a string, or for product_list the first three items joined like TechnicalSpecs does. */
function shown(key: string, value: unknown): string {
    if (key === "product_list") {
        return Array.isArray(value) ? value.slice(0, 3).filter(item => typeof item === "string").join(", ") : "";
    }
    return typeof value === "string" ? value : "";
}

/** True when the attribute gives the row a value of its own (present, not blank, not the template default). */
export function hasOwnValue(attributes: Record<string, unknown> | null | undefined, key: string, fallback: string): boolean {
    const text = shown(key, attributes?.[key]);
    return text.trim() !== "" && norm(text) !== norm(fallback);
}

/** A box renders only with at least this many real rows. */
export const MIN_REAL_ROWS = 2;

export interface ServiceRow { key: string; value: string }

function realRows(attributes: Record<string, unknown> | null | undefined, defaults: Record<string, string>): ServiceRow[] {
    return Object.entries(defaults)
        .filter(([key, fallback]) => hasOwnValue(attributes, key, fallback))
        .map(([key]) => ({ key, value: shown(key, attributes?.[key]).trim() }));
}

/** The Service Highlights rows to render (template order), or [] when the box is not rendered. */
export function serviceHighlightsRows(attributes: Record<string, unknown> | null | undefined): ServiceRow[] {
    const rows = realRows(attributes, SERVICE_HIGHLIGHTS_DEFAULTS);
    return rows.length >= MIN_REAL_ROWS ? rows : [];
}

/** The Service Snapshot rows to render (template order), or [] when the box is not rendered. */
export function serviceSnapshotRows(attributes: Record<string, unknown> | null | undefined): ServiceRow[] {
    const rows = realRows(attributes, SERVICE_SNAPSHOT_DEFAULTS);
    return rows.length >= MIN_REAL_ROWS ? rows : [];
}

/** Render "Service Highlights" only with at least MIN_REAL_ROWS real rows. */
export const showServiceHighlights = (attributes: Record<string, unknown> | null | undefined): boolean =>
    serviceHighlightsRows(attributes).length > 0;

/** Render "Service Snapshot" only with at least MIN_REAL_ROWS real rows. */
export const showServiceSnapshot = (attributes: Record<string, unknown> | null | undefined): boolean =>
    serviceSnapshotRows(attributes).length > 0;
